import { FileArrowDown } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Button } from 'core/components';
import { FileType } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridTableContext } from 'core/states';
import { QuotePartExportType } from 'quotes/components/QuotePartList';
import { QuoteType } from 'quotes/enums';
import { ALL_SPA_AUDIENCE_RIGHTS, VendorQuoteHelper } from 'quotes/helpers';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';
import { Color, Size, TooltipPosition, Variant } from 'theme/enums';

import { BulkExportSkuPartsWarning } from './BulkExportSkuPartsWarning';

export const BulkExportSkuPartsButton = () => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { selectedRows } = useAgGridTableContext();
  const { handleCliJobExport } = useCliJobHandler();
  const { exportBulkVendorQuoteSkuParts } = useQuoteActions();

  // Categorize the selection (fail-closed): an imported alternate VQ exports only when its
  // persisted Attributes pass the same preflight the BE runs (getSpaSendBlockReason); one that
  // fails blocks the batch; non-imported rows are omitted.
  const { eligible, blockers, omits } = useMemo(() => {
    const eligible: IVendorQuote[] = [];
    const blockers: IVendorQuote[] = [];
    const omits: IVendorQuote[] = [];

    (selectedRows as IVendorQuote[]).forEach((quote) => {
      if (!VendorQuoteHelper.isSkuExportAvailable({ quote, user })) {
        omits.push(quote);
      } else if (null === VendorQuoteHelper.getSpaSendBlockReason(quote, ALL_SPA_AUDIENCE_RIGHTS)) {
        eligible.push(quote);
      } else {
        blockers.push(quote);
      }
    });

    return { eligible, blockers, omits };
  }, [selectedRows, user]);

  // Enabled whenever >= 1 imported alternate VQ is selected (blockers included, per decision Z).
  const isDisabled = eligible.length + blockers.length === 0;

  const onExport = () => {
    setIsWarningOpen(false);
    handleCliJobExport(exportBulkVendorQuoteSkuParts, {
      quoteIds: eligible.map((quote) => quote.id),
      options: {
        partExportType: QuotePartExportType.EXTERNAL,
        fileType: FileType.CSV,
        quoteType: QuoteType.VENDOR,
      },
    });
  };

  const onClick = () => {
    if (blockers.length === 0 && omits.length === 0) {
      onExport();

      return;
    }

    setIsWarningOpen(true);
  };

  return (
    <>
      <Button
        text={t('buttonLabelExportSkuParts')}
        onClick={onClick}
        disabled={isDisabled}
        variant={Variant.OUTLINED}
        startDecorator={<FileArrowDown />}
        tooltipProps={
          isDisabled
            ? {
                placement: TooltipPosition.TOP,
                color: Color.NEUTRAL,
                size: Size.SM,
                title: t('tooltipBulkExportSkuPartsDisabled'),
                children: null,
              }
            : undefined
        }
        // MUI swallows hover on disabled buttons; re-enable pointer events so the tooltip shows.
        sx={
          isDisabled
            ? {
                pointerEvents: 'auto !important',
                cursor: 'not-allowed !important',
                '&:hover': { backgroundColor: 'transparent !important' },
              }
            : undefined
        }
      />

      {isWarningOpen && (
        <BulkExportSkuPartsWarning
          blockers={blockers}
          omits={omits}
          eligibleCount={eligible.length}
          onExport={onExport}
          onCancel={() => setIsWarningOpen(false)}
        />
      )}
    </>
  );
};
